/**
 * Storage wrapper with automatic in-memory fallback.
 *
 * Provides Storage API implementation that gracefully falls back to in-memory
 * storage when browser storage is unavailable (privacy mode, quota exceeded,
 * disabled by user).
 *
 * Implements full Storage interface for seamless compatibility. Automatically
 * tests storage availability and switches to fallback if needed.
 *
 * @example
 * ```typescript
 * const storage = new StorageFactory(() => localStorage);
 * storage.setItem('key', 'value'); // Uses localStorage or falls back to memory
 * ```
 */
export class StorageFactory {
  #inMemoryStorage: { [key: string]: string} = {};
  readonly #getStorage: () => Storage;

  constructor(getStorage: () => Storage) {
    this.#getStorage = getStorage;
  }

  clear(): void {
    if (this.#isSupported()) {
      this.#getStorage().clear();
    } else {
      this.#inMemoryStorage = {};
    }
  }

  getItem(name: string): string | null {
    if (this.#isSupported()) {
      return this.#getStorage().getItem(name);
    }
    if (Object.prototype.hasOwnProperty.call(this.#inMemoryStorage, name)) {
      return this.#inMemoryStorage[name];
    }
    return null;
  }

  setItem(name: string, value: string | number): void {
    const stringValue = value.toString();
    if (this.#isSupported()) {
      this.#getStorage().setItem(name, stringValue);
    } else {
      this.#inMemoryStorage[name] = stringValue;
    }
  }

  removeItem(name: string): void {
    if (this.#isSupported()) {
      this.#getStorage().removeItem(name);
    } else {
      delete this.#inMemoryStorage[name];
    }
  }

  key(index: number): string | null {
    if (this.#isSupported()) {
      return this.#getStorage().key(index);
    }
    return Object.keys(this.#inMemoryStorage)[index] || null;
  }

  length(): number {
    if (this.#isSupported()) {
      return this.#getStorage().length;
    }
    return Object.keys(this.#inMemoryStorage).length;
  }

  #isSupported(): boolean {
    try {
      const testKey = '__some_random_key_you_are_not_going_to_use__';
      this.#getStorage().setItem(testKey, testKey);
      this.#getStorage().removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }
}
